import DocumentViewerModal from "components/DocumentViewer";
import FileView from "components/fileView";
import DynamicFormInput from "components/form/form";
import FileUpload from "components/FileUpload";
import { useTranslation } from "react-i18next";
import TabNav from "components/TabNav";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useRef, useState } from "react";
import { updateSectorDirective } from "services/sectorDirectives";
import ShowUI, { LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { LoadingIcon } from "utils/constant";
import { SECTOR_DIRECTIVES_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { formatDate, formatDay } from "utils/helpers";
import LegislativeDraftRightPanel from "./fragments/rightPanel";
import { appendOfficeStamp } from "services/stampService";

export default function RegistrarLegislativeDraft({ request }: { request: any }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(t('ResearchFinding'));
    const [letterRefNum, setLetterRefNum] = useState("");
    const [completionFile, setCompletionFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    const contextData = useAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [formState, setFormState] = useState({ ...request });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLetterRefNum(e.target.value);
    };

    const handleSave = async () => {
        setSubmitting(true);
        const data = new FormData();
        data.append(
            "status",
            SECTOR_DIRECTIVES_REQUEST_STATUS.letter_refnum_submitted.value
        );
        data.append(
            "forward_status",
            SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value
        );
        data.append("registry_number", letterRefNum);
        if (completionFile) {
            data.append("completion_letter_files", completionFile);
            // Office stamp from HRMS: the backend stamps the PDF before storing it.
            await appendOfficeStamp(data);
        }

        try {
            const response = await updateSectorDirective(formState.id, data, contextData?.token || contextData?.user?.access_token || "");
            setFormState({ ...response });
            showToast(t("SavedSuccessfully"), "success");
            setLetterRefNum("");
            setCompletionFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateRequest"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    // Written comments files — sorted newest first
    const writtenFiles = (formState?.uploaded_files || [])
        .filter((d: any) => d.document_type === "written_comments_file")
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const currentWrittenFile = writtenFiles[0];
    const previousWrittenFiles = writtenFiles.slice(1);

    // Completion letter files
    const completionFiles = (formState?.uploaded_files || []).filter(
        (d: any) => d.document_type === "accurecy_completion_letter_file"
    );

    useEffect(() => { console.log(formState); }, [formState]);

    return (
        <div>
            <div className="flex mb-4 gap-2 font-semibold text-2xl text-[#696969]">
                <svg className="my-auto" xmlns="http://www.w3.org/2000/svg" width="24" height="20" viewBox="0 0 24 20" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0.335938 10.815C3.32099 11.9763 6.47113 12.6575 9.66929 12.8334V15.1666H14.3359V12.8334C17.5264 12.6335 20.6706 11.9693 23.6693 10.8616V19.8334H0.335938V10.815ZM15.5026 0L16.6693 1.16665V3.5H23.6693V9.57835C20.0921 10.914 16.3107 11.6205 12.4926 11.6666H11.6526C7.78322 11.6248 3.95185 10.898 0.335938 9.52V3.5H7.33594V1.16665L8.50259 0H15.5026ZM14.3359 2.33335H9.66929V3.5H14.3359V2.33335Z" fill="#696969" />
                </svg>
                <h1>{t('ConductLegislativeRequest')}</h1> {/* Already localized */}
            </div>

            <div className="flex gap-4">
                <div className="flex flex-col w-full gap-4">

                    {/* Draft Summary Card */}
                    <div className="max-w-4xl bg-white rounded-lg shadow-md p-6 h-fit"> <p className="font-semibold text-base mb-1">{t('ResearchDraftSummary')}</p>
                        <p className="text-[#B8B8B8] font-regular text-sm mb-1">{formatDate(formState?.created_at)}</p>
                        <p className="text-[#606060] font-regular text-base mb-1">{formState?.explanatory_notes}</p>
                        {formState?.uploaded_files?.find((document: any) => document.document_type === "directive_file") &&
                            <DocumentViewerModal
                                documents={formState?.uploaded_files.filter((document: any) => document.document_type === "directive_file")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        }
                    </div>

                    {/* Written Comments Card */}
                    {currentWrittenFile && (
                        <div className="max-w-4xl bg-white rounded-lg shadow-md p-6 h-fit">
                            <p className="font-semibold text-base mb-1">{t('WrittenComments')}</p>
                            <p className="text-[#B8B8B8] font-regular text-sm mb-3">{formatDay(formState?.created_at).rest}</p>
                            <p className="text-[#606060] font-regular text-base mb-3">{formState?.summary_of_comments}</p>
                            {/* Latest written comments file */}
                            <DocumentViewerModal
                                documents={[currentWrittenFile]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                            {/* Previous versions accordion */}
                            {previousWrittenFiles.length > 0 && <PreviousFilesAccordion files={previousWrittenFiles} />}
                        </div>
                    )}

                    {/* Completion Letter Files (already submitted) */}
                    {completionFiles.length > 0 && (
                        <div className="max-w-4xl bg-white rounded-lg shadow-md p-6 h-fit">
                            <p className="font-semibold text-base mb-3">{t('CompletionLetter')}</p>
                            <DocumentViewerModal
                                documents={completionFiles}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        </div>
                    )}
                </div>

                {/* Registrar Action Panel */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_reference_number.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                > {/* Assuming "Research Finding" is a key in en.ts */}
                    {activeTab === t('ResearchFinding') &&
                        <div className="flex w-full gap-8">
                            <div className="flex flex-col flex-1 max-w-4xl bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4 h-fit">
                                {/* Official header */}
                                <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                                {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                                            </p>
                                            <h3 className="text-white font-bold text-base">{t('RegistrarActions')}</h3>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5 flex flex-col gap-5">
                                    {/* Letter Reference Number */}
                                    <div>
                                        <label htmlFor="letter_reference_number" className="block text-sm font-semibold text-[#0A1D39] mb-1.5">
                                            {t('LetterReferenceNumber')} <span className="text-[#9B0505]">*</span>
                                        </label>
                                        <p className="text-xs text-gray-500 mb-2">
                                            {t("LetterReferenceNumberHint") || "The official registry reference number assigned to this request."}
                                        </p>
                                        <input
                                            id="letter_reference_number"
                                            type="text"
                                            value={letterRefNum}
                                            onChange={handleChange}
                                            placeholder={t("LetterReferenceNumberPlaceholder") || "Eg. 12345"}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A1D39] focus:border-transparent text-sm"
                                        />
                                    </div>

                                    {/* Completion Letter File Upload */}
                                    <div>
                                        <label className="block text-sm font-semibold text-[#0A1D39] mb-1.5">
                                            {t('CompletionLetterFile')}
                                        </label>
                                        <p className="text-xs text-gray-500 mb-2">
                                            {t("ResearchCompletionLetterHint") || "Upload the signed official completion letter."}
                                        </p>
                                        <FileUpload
                                            label={t('UploadCompletionLetter')}
                                            onChange={(file) => setCompletionFile(file as File)}
                                            ref={fileInputRef}
                                        />
                                        {completionFile && (
                                            <p className="text-xs text-gray-500 mt-1">{t('Selected')}: {completionFile.name}</p>
                                        )}
                                    </div>

                                    <button
                                        onClick={handleSave}
                                        disabled={submitting}
                                        className="w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                                    >
                                        {submitting ? (
                                            <>{LoadingIcon} {t('Submitting')}...</>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                {t('Save')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    }
                </ShowUI>
            </div>
        </div>
    );
}

// Accordion for previous file versions
function PreviousFilesAccordion({ files }: { files: any[] }) {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    return (
        <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-sm text-gray-600 font-medium"
            >
                <span>{files.length} {t('previous_version', { count: files.length })}</span>
                <svg
                    className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className="bg-white px-4 py-3 space-y-2 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('PreviousVersions')}</p>
                    {files.map((doc: any) => (
                        <div key={doc.document_uuid} className="border border-gray-100 rounded-lg p-2 bg-gray-50">
                            <p className="text-xs text-gray-400 mb-1">
                                {doc.created_at ? new Date(doc.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : ""}
                            </p>
                            <DocumentViewerModal
                                documents={[doc]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
